import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import config from '@/shared/config';
import { getActiveAppLocale } from '@/shared/utils/intl-locale';
import { logger } from '@/shared/utils/logger';

/**
 * Custom error interface for axios errors with additional properties
 */
interface CustomAxiosError extends Error {
  isOfflineError?: boolean;
  isNetworkError?: boolean;
  isPermissionError?: boolean;
  permissionRequired?: string;
  config?: unknown;
  response?: unknown;
}

/**
 * Extended request config with retry flag
 */
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Token refresh callback bridge - set by React layer
let refreshTokenCallback: (() => Promise<string | null>) | null = null;
let isRefreshing = false;
let failedRequestsQueue: {
  resolve: (value: unknown) => void;
  reject: (error: unknown) => void;
}[] = [];

// Rate limit tracking
let rateLimitedUntil: number | null = null;
const RATE_LIMIT_BACKOFF_MS = 60000; // 1 minute backoff on 429

// Track interceptor IDs to allow removal on reconfiguration
let requestInterceptorId: number | null = null;
let responseInterceptorId: number | null = null;

/**
 * Set the token refresh callback from React layer
 * This bridges the gap between the plain axios module and React's Auth0 hooks
 */
export const setRefreshTokenCallback = (
  callback: (() => Promise<string | null>) | null,
) => {
  refreshTokenCallback = callback;
};

/**
 * Process queued requests after token refresh completes
 */
const processQueue = (error: unknown | null, token: string | null = null) => {
  failedRequestsQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedRequestsQueue = [];
};

/**
 * Axios client instance with default configuration
 * Used across all API service files
 */

// Create the axios instance with default configuration
export const axiosInstance = axios.create({
  baseURL: config.apiServerUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Configure axios instance with authentication token
 * Called by AxiosAuthProvider when user authentication state changes
 */
export const configureAxios = (token: string | null) => {
  // Remove existing interceptors before adding new ones
  if (requestInterceptorId !== null) {
    axiosInstance.interceptors.request.eject(requestInterceptorId);
  }
  if (responseInterceptorId !== null) {
    axiosInstance.interceptors.response.eject(responseInterceptorId);
  }

  // Configure request interceptor
  requestInterceptorId = axiosInstance.interceptors.request.use(
    config => {
      try {
        // Check if user is offline before making request
        if (!navigator.onLine) {
          const offlineError = new Error(
            'No internet connection',
          ) as CustomAxiosError;
          offlineError.isOfflineError = true;
          logger.warn('Request blocked - user offline:', config.url);
          return Promise.reject(offlineError);
        }

        // Set headers
        config.headers = config.headers || {};

        // Advertise the active app locale so the backend can resolve a request
        // locale (Phase 1: parsed into req.locale for future localization).
        if (!config.headers['X-Donvio-Locale']) {
          config.headers['X-Donvio-Locale'] = getActiveAppLocale();
        }

        // Only add Authorization header if token is available AND not already set
        // This allows components to pass their own fresh token (e.g., ImageUpload after auth refresh)
        if (token && !config.headers['Authorization']) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }

        logger.debug('Request config:', {
          url: config.url,
          headers: config.headers,
        });

        return config;
      } catch (error) {
        logger.error('Error in axios interceptor:', error);
        return Promise.reject(error);
      }
    },
    error => {
      logger.error('Request interceptor error:', error);
      return Promise.reject(error);
    },
  );

  // Add response interceptor for error handling with token refresh
  responseInterceptorId = axiosInstance.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as ExtendedAxiosRequestConfig;

      // Flag offline errors
      if (!navigator.onLine) {
        (error as CustomAxiosError).isOfflineError = true;
        logger.warn('Response error - user offline');
        return Promise.reject(error);
      }

      // Flag network errors (no response from server)
      if (!error.response) {
        (error as CustomAxiosError).isNetworkError = true;
        error.message =
          error.message || 'Network error - unable to reach server';
        logger.error('Network error:', {
          url: error.config?.url,
          message: error.message,
        });
        return Promise.reject(error);
      }

      // Handle 429 Too Many Requests - rate limiting
      if (error.response.status === 429) {
        logger.warn('Rate limited (429):', {
          url: error.config?.url,
          retryAfter: error.response.headers?.['retry-after'],
        });

        // Set rate limit backoff
        rateLimitedUntil = Date.now() + RATE_LIMIT_BACKOFF_MS;

        // Dispatch event for UI to handle
        window.dispatchEvent(
          new CustomEvent('api:rate-limited', {
            detail: {
              url: error.config?.url,
              retryAfter: error.response.headers?.['retry-after'] || '60 seconds',
            },
          }),
        );

        return Promise.reject(error);
      }

      // Handle 401 Unauthorized - attempt token refresh
      if (error.response.status === 401 && originalRequest) {
        // Prevent infinite retry loop
        if (originalRequest._retry) {
          logger.error('401 after token refresh - forcing logout');
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
          return Promise.reject(error);
        }

        // Skip token refresh if we're currently rate limited
        if (rateLimitedUntil && Date.now() < rateLimitedUntil) {
          logger.warn('Skipping token refresh - currently rate limited');
          return Promise.reject(error);
        }

        // If already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedRequestsQueue.push({
              resolve: newToken => {
                if (newToken && originalRequest.headers) {
                  originalRequest.headers['Authorization'] =
                    `Bearer ${newToken}`;
                  resolve(axiosInstance(originalRequest));
                } else {
                  reject(error);
                }
              },
              reject,
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        // Attempt token refresh via callback
        if (refreshTokenCallback) {
          try {
            const newToken = await refreshTokenCallback();
            if (newToken) {
              if (originalRequest.headers) {
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              }
              processQueue(null, newToken);
              return axiosInstance(originalRequest);
            } else {
              // Token refresh returned null - session invalid
              processQueue(new Error('Token refresh failed'), null);
              window.dispatchEvent(new CustomEvent('auth:session-expired'));
              return Promise.reject(error);
            }
          } catch (refreshError) {
            // Check if the refresh itself was rate limited
            if ((refreshError as AxiosError)?.response?.status === 429) {
              rateLimitedUntil = Date.now() + RATE_LIMIT_BACKOFF_MS;
              logger.warn('Token refresh rate limited - backing off');
            }
            processQueue(refreshError, null);
            window.dispatchEvent(new CustomEvent('auth:session-expired'));
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        } else {
          // No refresh callback available - force logout
          logger.warn('No refresh callback available - forcing logout');
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
          isRefreshing = false;
          return Promise.reject(error);
        }
      }

      // Handle 403 Forbidden - permission denied
      if (error.response.status === 403) {
        const customError = error as CustomAxiosError;
        customError.isPermissionError = true;
        customError.permissionRequired = (
          error.response?.data as { requiredPermission?: string }
        )?.requiredPermission;

        logger.warn('Permission denied:', {
          url: error.config?.url,
          permission: customError.permissionRequired,
        });

        // Dispatch event for UI to handle (toast notifications, etc.)
        window.dispatchEvent(
          new CustomEvent('auth:permission-denied', {
            detail: {
              url: error.config?.url,
              permission: customError.permissionRequired,
              message:
                (error.response?.data as { error?: string })?.error ||
                'Permission denied',
            },
          }),
        );
      }

      return Promise.reject(error);
    },
  );
};

/**
 * Reset axios interceptors and state - primarily for testing
 */
export const resetAxiosInterceptors = () => {
  if (requestInterceptorId !== null) {
    axiosInstance.interceptors.request.eject(requestInterceptorId);
    requestInterceptorId = null;
  }
  if (responseInterceptorId !== null) {
    axiosInstance.interceptors.response.eject(responseInterceptorId);
    responseInterceptorId = null;
  }
  isRefreshing = false;
  failedRequestsQueue = [];
  rateLimitedUntil = null;
};

// Export configured instance as default for backward compatibility
export default axiosInstance;
